/**
 * ONE-SHOT PASSWORD-RECOVERY PROVENANCE HANDOFF (BF-A).
 *
 * `src/integrations/supabase/client.ts` builds a shared module singleton whose
 * auth client initializes (and therefore consumes any recovery callback in the
 * URL) as soon as that module is evaluated. Installed supabase-js defaults
 * apply: implicit flow with `detectSessionInUrl` enabled. A subscriber that
 * only registers when the `/reset-password` route mounts can therefore MISS the
 * `PASSWORD_RECOVERY` event entirely — late subscribers receive
 * `INITIAL_SESSION`, never a replay — and a genuine recovery link would fail
 * closed after the route's timeout.
 *
 * This module subscribes at the earliest safe point: its own evaluation. It is
 * the FIRST import declaration in `src/main.tsx`, so ES module evaluation order
 * guarantees that this module — and the shared client it imports — are fully
 * evaluated, with the listener already registered, before `App` or any other
 * module in the entry graph is evaluated. The guarantee comes from source
 * order, not from any assumption about supabase-js deferring `_initialize`.

 *
 * Security properties:
 *  - ONLY a genuine `PASSWORD_RECOVERY` event with a session is ever recorded.
 *    `INITIAL_SESSION`, `SIGNED_IN`, `TOKEN_REFRESHED`, an ambient persisted
 *    session, or URL shape alone never produce provenance.
 *  - The proof is in-memory only for the CURRENT document load. Nothing is
 *    written to localStorage/sessionStorage/cookies. The ONLY material retained
 *    is the recovered account's user id and the pathname the callback landed
 *    on — never a token, code, session object, email or any other secret.
 *  - It is single-use: `consumeRecoveryProvenance()` clears it, so a remount,
 *    a later navigation, or a back/forward step cannot replay the proof.
 *  - It expires in memory after `RECOVERY_PROVENANCE_TTL_MS` (the recovery
 *    exchange bound) so an unconsumed proof cannot linger indefinitely.
 *  - It is cleared on `popstate` (any history navigation) as well.
 */
// Google G1 (Codex closure #1): scrub Google's OAuth `?code`/`?state` from the
// address bar BEFORE './client' is evaluated, so the auth client's URL
// detection can never see, exchange or act on a Google authorization code.
// This dependency has no Supabase imports (pure constants only), which keeps
// this module the first Supabase-initializing import in main.tsx.
import '@/lib/google/callbackCapture';
import { supabase } from './client';

type Listener = () => void;

/**
 * In-memory lifetime of a captured proof. Never longer than the route's
 * recovery-exchange bound.
 */
export const RECOVERY_PROVENANCE_TTL_MS = 8000;

export interface RecoveryProof {
  /** The recovered account's user id. No token, session or email is retained. */
  userId: string;
  /** The pathname the recovery callback landed on. */
  path: string;
}

let pending: (RecoveryProof & { capturedAt: number }) | null = null;
let installed = false;
const listeners = new Set<Listener>();

function now(): number {
  return Date.now();
}

function expired(): boolean {
  return !!pending && now() - pending.capturedAt > RECOVERY_PROVENANCE_TTL_MS;
}

/** Clear any captured proof. Safe to call repeatedly. */
export function clearRecoveryProvenance(): void {
  pending = null;
}

/**
 * Consume the one-shot proof. Returns the bound proof at most once per captured
 * `PASSWORD_RECOVERY` event, and only for the path the callback landed on.
 */
export function consumeRecoveryProvenance(path?: string): RecoveryProof | null {
  if (!pending) return null;
  if (expired()) {
    clearRecoveryProvenance();
    return null;
  }
  if (path !== undefined && pending.path !== path) return null;
  const proof: RecoveryProof = { userId: pending.userId, path: pending.path };
  clearRecoveryProvenance();
  return proof;
}

/** True when a proof is currently held (does not consume it). Test/diagnostic use. */
export function hasPendingRecoveryProvenance(): boolean {
  if (expired()) clearRecoveryProvenance();
  return pending !== null;
}

/**
 * Observe recovery proofs captured AFTER subscription. The callback receives no
 * session or token material — the consumer must still call
 * `consumeRecoveryProvenance()` to take the single-use proof.
 */
export function subscribeRecoveryProvenance(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function record(userId: string, path: string): void {
  pending = { userId, path, capturedAt: now() };
  for (const l of Array.from(listeners)) {
    try { l(); } catch { /* listener failures must never affect auth flow */ }
  }
}

/**
 * Install the capture. Idempotent; called at module evaluation so no event can
 * be lost between client bootstrap and route mount.
 */
export function installRecoveryProvenanceCapture(): void {
  if (installed) return;
  installed = true;

  supabase.auth.onAuthStateChange((event, session) => {
    // A genuine recovery exchange completed by the SDK is the ONLY proof.
    if (event !== 'PASSWORD_RECOVERY' || !session) return;
    const userId = (session as { user?: { id?: string } } | null)?.user?.id;
    if (!userId) return;
    record(userId, typeof window !== 'undefined' ? window.location.pathname : '');
  });

  if (typeof window !== 'undefined') {
    // A history navigation invalidates any un-consumed proof.
    window.addEventListener('popstate', () => clearRecoveryProvenance());
  }
}

installRecoveryProvenanceCapture();
